// src/services/grammarTopicsMap.js

/**
 * Complete Grammar Topics Structure for All Levels
 * IDs match the pillarIdToTopicMap values used in Firebase
 */
export const pillarIdToTopicMap = {
  'A2-1': 'cases',
  'A2-2': 'adjectives_a2',
  'A2-3': 'perfect_a2',
  'A2-4': 'simple_past_a2',
  'A2-5': 'reflexive_a2',
  'A2-6': 'separable_a2',
  'A2-7': 'subordinate_a2',
  'A2-8': 'relative_a2',
  'A2-9': 'two_way_prepositions_a2',
  'A2-10': 'subjunctive_polite_a2',
  'A2-11': 'numbers_a2',
  'B1-1': 'connectors',
  'B1-2': 'passive',
  'B1-3': 'subjunctive',
  'B1-4': 'relative-clauses',
  'B1-5': 'prepositions',
  'B1-6': 'adjectives',
  'B1-7': 'genitive',
  'B1-8': 'verb-cases',
  'B1-9': 'infinitive-zu',
  'B1-10': 'noun-verb-connections',
  'B1-11': 'participles-adjectives',
  'B2-1': 'connectors_b2',
  'B2-2': 'moods_and_voice_b2',
  'B2-3': 'nominal_structures_b2',
  'B2-4': 'stylistic_refinement_b2',
  'B2-5': 'pronouns_b2',
  'B2-6': 'prepositions_b2',
  'B2-7': 'adjectives_b2',
  'B2-8': 'tenses_b2',
  'B2-9': 'cases_b2',
  'C1-1': 'connectors_c1',
  'C1-2': 'nominal_vs_verbal_styles',
  'C1-3': 'passive_alternatives',
  'C1-4': 'subjunctive_moods_c1',
  'C1-5': 'participial_constructions_c1',
  'C1-6': 'subjective_use_of_modal_verbs',
  'C1-7': 'noun_verb_combinations',
  'C1-8': 'complex_adjective_declension_c1',
};

// Create reverse mapping: topicId -> {level, pillar}
const topicToPillarMap = {};
for (const [key, value] of Object.entries(pillarIdToTopicMap)) {
  const [level, pillar] = key.split('-');
  topicToPillarMap[value] = { level: level.toLowerCase(), pillar };
}

/**
 * Convert topic ID to grammar lesson URL
 * @param {string} topicId - e.g., 'connectors', 'passive', 'cases'
 * @returns {string} - e.g., '/lessons/b1?pillar=1'
 */
export const getGrammarLessonUrl = (topicId) => {
  const mapping = topicToPillarMap[topicId];

  if (!mapping) {
    console.warn(`No pillar mapping found for topic: ${topicId}`);
    return '/practice/grammar'; // Fallback
  }

  return `/lessons/${mapping.level}?pillar=${mapping.pillar}`;
};
export const GRAMMAR_TOPICS_BY_LEVEL = {
  A2: {
    totalTopics: 11,
    topics: [
      {
        id: 'cases',
        name: 'Cases (Kasus)',
        category: 'Core Grammar',
        subtopics: [
          'Nominativ – The Star of the Show (Subject)',
          'Akkusativ – The Direct Receiver (Direct Object)',
          'Dativ – The Indirect Receiver (To Whom?)',
          'Genitiv – The Owner (Whose?)'
        ]
      },
      {
        id: 'adjectives_a2',
        name: 'Adjective Declension',
        category: 'Adjectives',
        subtopics: [
          'The Adjective\'s Wardrobe System',
          'Weak Declension – After Definite Articles',
          'Mixed Declension – After Indefinite Articles',
          'Strong Declension – With No Article',
          'Special Cases & Advanced Usage'
        ]
      },
      {
        id: 'perfect_a2',
        name: 'Perfect Tense (Perfekt)',
        category: 'Tenses',
        subtopics: [
          'The Sentence-Sandwich System',
          'When to Use SEIN – Journeys & Transformations',
          'When to Use HABEN – The Default Helper',
          'Forming the Past Participle (Partizip II)',
          'Common Sentence Patterns & Word Order'
        ]
      },
      {
        id: 'simple_past_a2',
        name: 'Simple Past (Präteritum)',
        category: 'Tenses',
        subtopics: [
          'Two Ways to Tell a Story – Perfekt vs. Präteritum',
          'SEIN (to be) – The Most Important Präteritum',
          'HABEN (to have) – The Second Essential',
          'Modal Verbs – The Conversation Essentials',
          'Regular Verbs in Written German',
          'Irregular Verbs in Stories'
        ]
      },
      {
        id: 'reflexive_a2',
        name: 'Reflexive Verbs',
        category: 'Verbs',
        subtopics: [
          'The Mirror Concept – Understanding Reflexive Actions',
          'Accusative Reflexive Pronouns – The Standard Case',
          'Dative Reflexive Pronouns – The Passenger-Seat Rule',
          'Common Reflexive Verbs – Daily-Life Actions',
          'Reflexive Verbs with Prepositions',
          'Reflexive Verbs in Perfect Tense'
        ]
      },
      {
        id: 'separable_a2',
        name: 'Separable Verbs',
        category: 'Verbs',
        subtopics: [
          'The Lego-Block Concept – Understanding Separable Structure',
          'The Sentence Bracket (Satzklammer) – Main-Clause Pattern',
          'Staying Together – Modal Verbs',
          'Perfect Tense – The GE-Sandwich',
          'Subordinate Clauses – Staying United',
          'Common Separable Verbs – Daily-Life Champions'
        ]
      },
      {
        id: 'subordinate_a2',
        name: 'Subordinate Clauses',
        category: 'Sentence Structure',
        subtopics: [
          'The Train & Caboose System',
          'The Golden Rule – Verb to the End',
          'Formation 1: Engine First (Main + Subordinate)',
          'Formation 2: Caboose First (Subordinate + Main)',
          'Special Cases – Modal Verbs & Separable Verbs',
          'Common Subordinating Conjunctions'
        ]
      },
      {
        id: 'relative_a2',
        name: 'Relative Clauses',
        category: 'Sentence Structure',
        subtopics: [
          'The Sticky-Note Concept',
          'The Two-Step Rule – Choosing the Right Pronoun',
          'Relative-Pronoun Table – Almost like Definite Articles',
          'Nominativ Relative Clauses – The Subject Role',
          'Akkusativ Relative Clauses – The Direct-Object Role',
          'Dativ Relative Clauses – The Indirect-Object Role',
          'Prepositions in Relative Clauses',
          'WO for Places – The Easy Shortcut'
        ]
      },
      {
        id: 'two_way_prepositions_a2',
        name: 'Two-Way Prepositions',
        category: 'Prepositions',
        subtopics: [
          'The Keys Analogy – Location vs. Direction',
          'The Golden Questions – Wo? vs. Wohin?',
          'The Nine Two-Way Prepositions',
          'Dative Case – Fixed Location (Wo?)',
          'Accusative Case – Movement / Direction (Wohin?)',
          'Verb Pairs – Same Action, Different Perspectives'
        ]
      },
      {
        id: 'subjunctive_polite_a2',
        name: 'Polite Requests (Konjunktiv II)',
        category: 'Modal',
        subtopics: [
          'The Politeness-Softener Concept',
          'WÜRDE + Infinitive – The Universal Tool',
          'KÖNNTE – Asking About Ability (Could you…?)',
          'HÄTTE – Polite Wishes for Objects (I would like…)',
          'Adding Extra Politeness – BITTE & VIELLEICHT',
          'Formal vs. Informal Politeness'
        ]
      },
      {
        id: 'numbers_a2',
        name: 'Numbers, Time & Dates',
        category: 'Practical',
        subtopics: [
          'The LEGO Number System',
          'Cardinal Numbers – The Building Blocks',
          'Prices & Measurements – Practical Applications',
          'Ordinal Numbers – Date-Building Blocks',
          'Dates with AM – The Special Ending System',
          'Formal Time – The 24-Hour System',
          'Informal Time – Conversational Expressions'
        ]
      }
    ]
  },

  B1: {
    totalTopics: 11,
    topics: [
      {
        id: 'connectors',
        name: 'Sentence Structure & Connectors',
        category: 'Sentence Structure',
        subtopics: [
          'ADUSO Group (Position 0)',
          'Inversion (Adverbs)',
          'Subordinate Clause',
          'Two-Part Connectors'
        ]
      },
      {
        id: 'passive',
        name: 'The Passive Voice',
        category: 'Voice & Mood',
        subtopics: [
          'Present Tense (werden + Partizip II)',
          'Simple Past (wurden + Partizip II)',
          'Perfect (sein + Partizip II + worden)',
          'Past Perfect (war + Partizip II + worden)',
          'With Modal Verb (Present)',
          'With Modal Verb (Past)',
          'Expressing the Agent'
        ]
      },
      {
        id: 'subjunctive',
        name: 'Konjunktiv II',
        category: 'Modal',
        subtopics: [
          'Polite Requests & Questions',
          'Giving Advice',
          'Hypothetical Situations',
          'Wishes',
          'Unreal Past Conditions',
          'Verb Forms'
        ]
      },
      {
        id: 'relative-clauses',
        name: 'Relative Clauses',
        category: 'Sentence Structure',
        subtopics: [
          'The Two-Step Rule',
          'Nominativ Case',
          'Akkusativ Case',
          'Dativ Case',
          'With Prepositions',
          'Special Pronouns (was, wo, wohin)'
        ]
      },
      {
        id: 'prepositions',
        name: 'Verbs with Fixed Prepositions',
        category: 'Verbs',
        subtopics: [
          'Prepositions with Accusative',
          'Prepositions with Dative',
          'Da-Compounds (things)',
          'Wo-Compounds (questions)',
          'Person vs. Thing distinction'
        ]
      },
      {
        id: 'adjectives',
        name: 'Adjective Declension',
        category: 'Adjectives',
        subtopics: [
          'Weak Declension (after der-words)',
          'Strong Declension (no article)',
          'Nominalized Adjectives'
        ]
      },
      {
        id: 'genitive',
        name: 'The Genitive Case',
        category: 'Cases',
        subtopics: [
          'Function & Purpose',
          'Article & Noun Changes',
          'Genitive Prepositions',
          'Spoken vs. Written',
          'Names & Proper Nouns',
          'Common Mistakes'
        ]
      },
      {
        id: 'verb-cases',
        name: 'Verbs with Case Complements',
        category: 'Verbs',
        subtopics: [
          'Verb as King – Case Government',
          'Dative-Only Verbs',
          'Dative + Accusative Verbs',
          'Word-Order Hierarchy',
          'Common Errors & Prevention',
          'Advanced Applications'
        ]
      },
      {
        id: 'infinitive-zu',
        name: 'Infinitive with zu',
        category: 'Verbs',
        subtopics: [
          'Core Function',
          'Trigger Verbs',
          'Noun + Verb Phrases',
          'Impersonal es ist',
          'Separable Verbs Rule',
          'Purpose – um...zu',
          'Modal Exception'
        ]
      },
      {
        id: 'noun-verb-connections',
        name: 'Noun-Verb Connections',
        category: 'Style',
        subtopics: [
          'Core Concept – Formality Upgrade',
          'Active Expressions',
          'Bringing Into State',
          'Passive/Result State',
          'Key Grammar & Mistakes'
        ]
      },
      {
        id: 'participles-adjectives',
        name: 'Participles as Adjectives',
        category: 'Adjectives',
        subtopics: [
          'Verb-to-Adjective Transformation',
          'Partizip I – Active/Ongoing',
          'Partizip II – Completed/Passive',
          'Sein-Verbs with Active Meaning',
          'Extended Participles',
          'Advanced Gerundiv',
          'Critical Mistakes'
        ]
      }
    ]
  },

  B2: {
    totalTopics: 9,
    topics: [
      {
        id: 'connectors_b2',
        name: 'Complex Sentences',
        category: 'Sentence Structure',
        subtopics: [
          'The Three Architectural Pillars of Connection',
          'Subordinating Conjunctions',
          'Two-Part Connectors',
          'Register Control',
          'Advanced Sentence Architecture'
        ]
      },
      {
        id: 'moods_and_voice_b2',
        name: 'The Verb in Command',
        category: 'Voice & Mood',
        subtopics: [
          'Passive Voice – The Perspective Shifter',
          'Passive Alternatives – The Elegance Tools',
          'Subjunctive II – The Diplomat\'s Tool',
          'Subjunctive I – The Reporter\'s Shield',
          'Register Control and Stylistic Mastery'
        ]
      },
      {
        id: 'nominal_structures_b2',
        name: 'Advanced Nominal Structures',
        category: 'Style',
        subtopics: [
          'The Nominalstil Transformation',
          'Nominalization',
          'The Genitive Case – Building Logical Architecture',
          'Extended Participle Constructions',
          'N-Declension',
          'Nominalstil Mastery'
        ]
      },
      {
        id: 'stylistic_refinement_b2',
        name: 'Stylistic Refinement',
        category: 'Style',
        subtopics: [
          'Modal Particles',
          'Nomen-Verb-Verbindungen',
          'Prepositional Conciseness',
          'Register Mastery'
        ]
      },
      {
        id: 'pronouns_b2',
        name: 'Pronouns – The Art of Substitution',
        category: 'Pronouns',
        subtopics: [
          'Personal Pronouns',
          'The Multifaceted "es"',
          'Possessive Pronouns',
          'Relative Pronouns',
          'Prepositional Relatives',
          'Demonstrative Pronouns',
          'Pronoun Mastery'
        ]
      },
      {
        id: 'prepositions_b2',
        name: 'Prepositions – Case and Meaning',
        category: 'Prepositions',
        subtopics: [
          'Two-Way Prepositions',
          'Dative Prepositions',
          'Accusative Prepositions',
          'Genitive Prepositions',
          'Prepositional Artistry'
        ]
      },
      {
        id: 'adjectives_b2',
        name: 'Adjectives in Detail',
        category: 'Adjectives',
        subtopics: [
          'Advanced Declension Mastery',
          'Nominalized Adjectives',
          'Sophisticated Comparison',
          'Adjective Complements',
          'Adjective Mastery'
        ]
      },
      {
        id: 'tenses_b2',
        name: 'Mastery of Time – Verb Tenses',
        category: 'Tenses',
        subtopics: [
          'The Past Tense Spectrum',
          'Present and Future',
          'The Deep Past (Plusquamperfekt)',
          'Future Tenses',
          'Tense Mastery'
        ]
      },
      {
        id: 'cases_b2',
        name: 'Advanced Cases',
        category: 'Cases',
        subtopics: [
          'Case System Mastery',
          'Advanced Case Applications',
          'Case in Context'
        ]
      }
    ]
  },

  C1: {
    totalTopics: 8,
    topics: [
      {
        id: 'connectors_c1',
        name: 'Advanced Sentence Connectors',
        category: 'Sentence Structure',
        subtopics: [
          'Coordinating Conjunctions',
          'Subordinating Conjunctions',
          'Adverbial Connectors',
          'Advanced Two-Part Connectors',
          'Modal Particles in Connective Function'
        ]
      },
      {
        id: 'nominal_vs_verbal_styles',
        name: 'Nominal vs. Verbal Styles',
        category: 'Style',
        subtopics: [
          'Kausal (Causal)',
          'Temporal (Temporal)',
          'Konzessiv (Concessive)',
          'Final (Final/Purpose)',
          'Functional Verb Constructions'
        ]
      },
      {
        id: 'passive_alternatives',
        name: 'The Passive Voice & Its Alternatives',
        category: 'Voice & Mood',
        subtopics: [
          'Vorgangspassiv (Processual Passive)',
          'Zustandspassiv (Statal Passive)',
          'Modal Alternative: sich lassen + Infinitiv',
          'Modal Alternative: sein + zu + Infinitiv',
          'Adjectival Alternatives',
          'Advanced Passive Alternatives'
        ]
      },
      {
        id: 'subjunctive_moods_c1',
        name: 'The Subjunctive Mood',
        category: 'Modal',
        subtopics: [
          'Konjunktiv I: Indirect Speech',
          'Konjunktiv II: Hypotheticals',
          'Advanced Subjunctive Constructions',
          'Modal Subjunctive Combinations'
        ]
      },
      {
        id: 'participial_constructions_c1',
        name: 'Participle Constructions',
        category: 'Advanced',
        subtopics: [
          'Partizip I (Present Participle)',
          'Partizip II (Past Participle)',
          'Gerundiv (zu + Partizip I)',
          'Advanced Participial Techniques'
        ]
      },
      {
        id: 'subjective_use_of_modal_verbs',
        name: 'Subjective Use of Modal Verbs',
        category: 'Modal',
        subtopics: [
          'Logical Deduction: müssen',
          'High Probability: dürfte/müsste',
          'Moderate Possibility: könnte/mag',
          'Reported Information: soll',
          'Self-Reported Claims: will',
          'Advanced Modal Combinations'
        ]
      },
      {
        id: 'noun_verb_combinations',
        name: 'Noun-Verb Combinations',
        category: 'Style',
        subtopics: [
          'Action Performance (Agentive)',
          'State Transitions (Causative)',
          'Passive Processes (Intransitive)',
          'Institutional Procedures',
          'Complex Aspectual Relationships'
        ]
      },
      {
        id: 'complex_adjective_declension_c1',
        name: 'Adjectives with Fixed Prepositions',
        category: 'Adjectives',
        subtopics: [
          'Emotional and Evaluative Adjectives',
          'Source and Relationship Adjectives',
          'Pronominal Adverbs',
          'Advanced Complement Patterns'
        ]
      }
    ]
  }
};

// Helper function to get total topics across all levels
export const getTotalTopicsCount = () => {
  return Object.values(GRAMMAR_TOPICS_BY_LEVEL).reduce(
    (sum, level) => sum + level.totalTopics,
    0
  );
};

// Helper function to get topics for a specific level
export const getTopicsForLevel = (level) => {
  return GRAMMAR_TOPICS_BY_LEVEL[level] || null;
};

/**
 * Get the display name for a grammar topic by level and pillar id (1-based).
 * Used for browser tab title on grammar practice/lessons.
 * @param {string} level - e.g. 'A2', 'B1', 'B2', 'C1'
 * @param {number} pillarId - 1-based pillar index
 * @returns {string|null} - Topic name from TOPICS_README / GRAMMAR_TOPICS_BY_LEVEL, or null
 */
export const getGrammarTopicTitle = (level, pillarId) => {
  const levelData = GRAMMAR_TOPICS_BY_LEVEL[level?.toUpperCase()];
  if (!levelData?.topics || !pillarId || pillarId < 1) return null;
  const topic = levelData.topics[pillarId - 1];
  return topic?.name ?? null;
};

// Helper function to get all topic IDs for tracking
export const getAllTopicIds = () => {
  const ids = [];
  Object.values(GRAMMAR_TOPICS_BY_LEVEL).forEach(level => {
    level.topics.forEach(topic => {
      ids.push(topic.id);
    });
  });
  return ids;
};

// Export total count
export const TOTAL_GRAMMAR_TOPICS = getTotalTopicsCount();